import React, { FC, useCallback, useMemo } from 'react'

import { useQuery } from '@apollo/client'
import {
  Box,
  RadioButton,
  type SelectOption,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  ContractsAnnualWorkingTimeDistributionEnum,
  ContractsBankHolidayTreatmentEnum,
  ContractsEmployeesContractVersionFragment,
  TaskDetailAffecteeReferenceContractFragment,
  WorkHoursStepDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import DaysSelector from 'modules/core/components/Inputs/DaysSelector'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import TextInput from 'modules/core/components/Inputs/Text'
import Loading from 'modules/core/components/Loading'
import NumberField from 'modules/core/components/NumberField'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { translations as workingHoursTranslations } from 'modules/contracts/components/FixFlow/lib/workingHoursTranslations'
import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import ContractTypeSelect from 'modules/contracts/components/FlowPage/Steps/LegalAndExtras/_ContractTypeSelect'
import useCanSeeYearlyBalance from 'modules/contracts/hooks/useCanSeeYearlyBalance'
import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import { translate } from 'modules/contracts/lib/translate'
import MatchingValueTooltip from 'modules/employees/components/UnifiedAddEmployeeModal/Fields/MatchingValueTooltip'
import { WorkingConditionsKeys, validateField } from 'modules/job_catalog/lib/workingConditions'

import MaximumHoursHint from './MaximumHoursHint'
import WorkHoursHint from './WorkHoursHint'
import {
  MAX_PERCENTAGE_WORKING_HOURS,
  MIN_PERCENTAGE_WORKING_HOURS,
  constraintToWorkingHoursTimePercentage,
  stepHasErrors,
} from './validator'

const CENTS = 100

interface Props {
  form: Form
  withFormWrapper?: boolean
  translations?: { [key: string]: string }
  step?: number
  type?: ContractFlowType
  contractVersion?:
    | TaskDetailAffecteeReferenceContractFragment
    | ContractsEmployeesContractVersionFragment
    | null
  setIsCurrentStepCompleted?: (isCompleted: boolean) => void
  legalEntityCountry?: string
  canSeeWorkingHours?: boolean
  canSeeContractType?: boolean
  hasContractVersions: boolean
  hasDevTimeTrackingWorkableDaysDefinition?: boolean
  hasTimeTrackingAnnualisationDailyValue?: boolean
  hasFixedDiscontinuousContracts?: boolean
}

export const WorkHours = observer(
  ({
    form,
    step,
    type = 'fix',
    legalEntityCountry,
    contractVersion,
    withFormWrapper,
    setIsCurrentStepCompleted,
    translations = workingHoursTranslations(),
    canSeeWorkingHours,
    canSeeContractType,
    hasContractVersions,
    hasDevTimeTrackingWorkableDaysDefinition,
    hasTimeTrackingAnnualisationDailyValue,
    hasFixedDiscontinuousContracts = false,
  }: Props) => {
    const { loading, canSeeYearlyBalance } = useCanSeeYearlyBalance()
    const { spanishContractData } = useLegalEntityContractData()

    const { data } = useQuery(WorkHoursStepDocument, {
      variables: {
        nodeUuid: form.get('job_catalog_tree_node_uuid').value,
      },
      skip: !form.has('job_catalog_tree_node_uuid'),
    })

    const workingConditions = useMemo(
      () =>
        data?.jobCatalog.nodeAttributesConnection.nodes.find(
          (attribute) => attribute.valueWorkingConditions && attribute.takesPrecedence
        )?.valueWorkingConditions,
      [data]
    )

    const suggestedWorkingConditionsValues = useMemo(() => {
      const maxYearlyHoursCents = workingConditions?.conditions?.find(
        (condition) => condition.key === WorkingConditionsKeys.MAX_ANNUAL_HOURS
      )?.value
      const maxWeeklyHoursCents = workingConditions?.conditions?.find(
        (condition) => condition.key === WorkingConditionsKeys.MAX_WEEKLY_HOURS
      )?.value
      const maxDailyHoursCents = workingConditions?.conditions?.find(
        (condition) => condition.key === WorkingConditionsKeys.MAX_DAILY_HOURS
      )?.value
      const minRestHoursBetweenDaysCents = workingConditions?.conditions?.find(
        (condition) => condition.key === WorkingConditionsKeys.MIN_REST_HOURS_BETWEEN_DAYS
      )?.value

      const convertCentsToUIValue = (hourCents?: string): string | undefined =>
        hourCents ? String(Number(hourCents) / 100) : undefined

      return {
        maxYearlyHours: convertCentsToUIValue(maxYearlyHoursCents),
        maxWeeklyHours: convertCentsToUIValue(maxWeeklyHoursCents),
        maxDailyHours: convertCentsToUIValue(maxDailyHoursCents),
        minRestHoursBetweenDays: convertCentsToUIValue(minRestHoursBetweenDaysCents),
        bankHolidaysTreatment: workingConditions?.conditions?.find(
          (condition) => condition.key === WorkingConditionsKeys.BANK_HOLIDAYS_TREATMENT
        )?.value,
      }
    }, [workingConditions])

    const FREQUENCIES: Record<string, string> = {
      week: i18n.t('common.week.one'),
      day: i18n.t('common.day.one'),
      month: i18n.t('common.month'),
      year: i18n.t('common.year'),
    }
    const workingHoursField = form.get('working_hours')
    const workingHoursFrequencyField = form.get('working_hours_frequency')
    const workingHoursValue = canSeeWorkingHours ? workingHoursField.value : ''
    const workingHoursFrequencyValue = canSeeWorkingHours ? workingHoursFrequencyField.value : ''
    const frequencyOptions: SelectOption[] = Object.keys(FREQUENCIES).map((key) => ({
      label: FREQUENCIES[key] ?? '',
      value: key,
    }))

    const workingDayTypes = spanishContractData.workingDayTypes
    const workingDayTypeField = form.get('es_working_day_type_id')

    const workingDayTypesOptions = workingDayTypes
      .filter((workingDayType) => !workingDayType.archivedAt)
      .map((x) => {
        return {
          label: x.name,
          value: x.id,
        }
      })

    const workingType = useCallback(() => {
      const id = form.get('es_working_day_type_id').value
      const option = workingDayTypesOptions.find((e) => String(e.value) === String(id))
      return option ? option.label : `-- ${i18n.t('common.none')} --`
    }, [form, workingDayTypesOptions])

    const workingHoursName = () => {
      const currentOption = frequencyOptions.find(
        (e) => e.value === form.get('working_hours_frequency').value
      )
      return currentOption?.label || `-- ${i18n.t('common.none')} --`
    }

    const isContractHoursSet = form.get('working_hours').value
    const isWorkingFrequencySet = form.get('working_hours_frequency').value

    const bankHolidayTreatmentOptions: {
      label: string
      value: ContractsBankHolidayTreatmentEnum
    }[] = [
      {
        label: translate(translations, 'non_workable') || '',
        value: 'non_workable',
      },
      {
        label: translate(translations, 'workable') || '',
        value: 'workable',
      },
    ]

    const annualWorkingTimeDistributionOptions: {
      value: ContractsAnnualWorkingTimeDistributionEnum
      label: string
      description: string
    }[] = [
      {
        value: 'limit_workdays',
        label: i18n.t(
          'add_employee_modal.contract_information_step.fields.annual_working_time_distribution.limit_workdays'
        ),
        description: i18n.t(
          'add_employee_modal.contract_information_step.fields.annual_working_time_distribution.limit_workdays_description'
        ),
      },
      {
        value: 'limit_daily_hours',
        label: i18n.t(
          'add_employee_modal.contract_information_step.fields.annual_working_time_distribution.limit_daily_hours'
        ),
        description: i18n.t(
          'add_employee_modal.contract_information_step.fields.annual_working_time_distribution.limit_daily_hours_description'
        ),
      },
    ]

    const isSpanish =
      contractVersion && type === 'fix'
        ? contractVersion?.country === 'es'
        : legalEntityCountry === 'es'
    const isFrench =
      contractVersion && type === 'fix'
        ? contractVersion?.country === 'fr'
        : legalEntityCountry === 'fr'
    const isGerman =
      contractVersion && type === 'fix'
        ? contractVersion?.country === 'de'
        : legalEntityCountry === 'de'
    const isPortuguese =
      contractVersion && type === 'fix'
        ? contractVersion?.country === 'pt'
        : legalEntityCountry === 'pt'
    const hasContractType = isSpanish || isFrench || isGerman || isPortuguese

    const isNewStartDateEnabled = form.get('starts_on').isDirty
    const canToggleDiscontinuous = !hasContractVersions || isNewStartDateEnabled

    const employmentFields = [
      {
        key: 'contract_type',
        component:
          canSeeContractType && hasContractType ? (
            <ContractTypeSelect
              form={form}
              type={type}
              contractVersionCountry={contractVersion?.country ?? undefined}
              legalEntityCountry={legalEntityCountry}
            />
          ) : null,
        priority: 1,
      },
      {
        key: 'discontinuous_toggle',
        component:
          hasFixedDiscontinuousContracts && canToggleDiscontinuous ? (
            <TogglerCheckbox
              information=""
              label={translate(translations, 'is_discontinuous') || ''}
              value={form.get('is_discontinuous').value}
              onChange={(checked: boolean) => form.get('is_discontinuous').set(checked)}
            />
          ) : null,
        priority: 2,
      },
      {
        key: 'working_day_type',
        component:
          legalEntityCountry === 'es' ? (
            <DropdownSearchBar
              name={workingType()}
              label={i18n.t(
                'add_employee_modal.contract_information_step.fields.working_day_type.label'
              )}
              onSelect={(item) => {
                workingDayTypeField?.set(item.value)
              }}
              labelNotFound={i18n.t(
                'contract_versions.forms.basic_information.work_day_type_not_found'
              )}
              data={workingDayTypesOptions}
              searchLabel={i18n.t(
                'contract_versions.forms.contract_extra_information.a3innuva.search_label'
              )}
              loading={loading}
              popoverWidth="s400"
            />
          ) : null,
        priority: 3,
      },
      {
        key: 'working_time_percentage',
        component: (
          <NumberField
            label={translate(translations, 'working_time_percentage_in_cents')}
            placeholder={translate(translations, 'working_time_percentage_placeholder')}
            value={
              form.get('working_time_percentage_in_cents').value
                ? form.get('working_time_percentage_in_cents').value / CENTS
                : undefined
            }
            onChange={(value: number | null) =>
              form
                .get('working_time_percentage_in_cents')
                .set(value !== null ? constraintToWorkingHoursTimePercentage(value) * CENTS : null)
            }
            step={1}
            min={MIN_PERCENTAGE_WORKING_HOURS}
            max={MAX_PERCENTAGE_WORKING_HOURS}
            indicator="%"
            maxDecimals={0}
          />
        ),
        priority: 4,
      },
    ]

    const sortedEmploymentFields = employmentFields
      .filter((field) => field.component !== null)
      .sort((a, b) => a.priority - b.priority)

    const maxLegalYearlyHoursValue = form.get('max_legal_yearly_hours')?.value
    const showAnnualWorkingTimeDistribution =
      hasTimeTrackingAnnualisationDailyValue && !!maxLegalYearlyHoursValue

    const maxLegalYearlyHours = form.get('max_legal_yearly_hours')?.value
    const maximumWeeklyHours = form.get('maximum_weekly_hours')?.value
    const maxWorkMinutesPerDay = form.get('max_work_minutes_per_day')?.value
    const minRestMinutesBetweenDays = form.get('min_rest_minutes_between_days')?.value

    useEffect(() => {
      if (canSeeWorkingHours) {
        workingHoursField.setErrors(null)
        workingHoursFrequencyField.setErrors(null)
      }

      const hasStepErrors = stepHasErrors(form)

      const hasWorkingConditionsValidationErrors = canSeeYearlyBalance
        ? validateField('ANNUAL_HOURS', maxLegalYearlyHours) !== undefined ||
          validateField('WEEKLY_HOURS', maximumWeeklyHours) !== undefined ||
          validateField('DAILY_HOURS', maxWorkMinutesPerDay) !== undefined ||
          validateField('REST_HOURS_BETWEEN_DAYS', minRestMinutesBetweenDays) !== undefined
        : false

      setIsCurrentStepCompleted?.(!hasStepErrors && !hasWorkingConditionsValidationErrors)
    }, [
      workingHoursValue,
      workingHoursFrequencyValue,
      workingHoursField,
      workingHoursFrequencyField,
      form,
      setIsCurrentStepCompleted,
      canSeeWorkingHours,
      canSeeYearlyBalance,
      maxLegalYearlyHours,
      maximumWeeklyHours,
      maxWorkMinutesPerDay,
      minRestMinutesBetweenDays,
    ])

    if (!canSeeWorkingHours) return <></>
    if (loading) return <Loading />

    return hasDevTimeTrackingWorkableDaysDefinition ? (
      <Box gap="s24">
        <FormWrapper
          withFormWrapper={withFormWrapper}
          hint={
            step && (
              <WorkHoursHint
                step={step}
                type={type}
                isContractHoursSet={isContractHoursSet}
                isWorkingFrequencySet={isWorkingFrequencySet}
              />
            )
          }
        >
          <Box gap="s16">
            <Box gap="s16">
              <Box flexDirection="row" gap="s16" width="full">
                <Box
                  width="full"
                  flexDirection="row"
                  alignItems="flexEnd"
                  gap="s8"
                  position="relative"
                >
                  <TextInput
                    field={form.get('working_hours')}
                    type="number"
                    min={0}
                    label={translate(translations, 'hours')}
                    placeholder={translate(translations, 'enter')}
                  />
                </Box>
                <Box width="full">
                  <DropdownSearchBar
                    name={workingHoursName()}
                    label={translate(translations, 'period') || ''}
                    onSelect={(item) => {
                      form.get('working_hours_frequency').set(item.value)
                    }}
                    labelNotFound={i18n.t(
                      'contract_versions.forms.basic_information.option_not_found'
                    )}
                    data={frequencyOptions}
                    searchLabel={i18n.t(
                      'contract_versions.forms.contract_extra_information.a3innuva.search_label'
                    )}
                    popoverWidth="s300"
                  />
                </Box>
              </Box>
              <Box gap="s8">
                <Text.Normal color="grey1000" weight="medium" size="200">
                  {translate(translations, 'days')}
                </Text.Normal>
                <Box paddingLeft="s24" alignItems="flexStart">
                  <DaysSelector field={form.get('working_week_days')} canUnselectAll />
                </Box>
              </Box>

              {canSeeYearlyBalance && (
                <Box gap="s16">
                  <Box flexDirection="row" alignItems="flexEnd" gap="s8" position="relative">
                    <TextInput
                      field={form.get('max_legal_yearly_hours')}
                      type="number"
                      min={0}
                      label={translate(translations, 'max_annual_hours')}
                      error={validateField(
                        'ANNUAL_HOURS',
                        form.get('max_legal_yearly_hours').value
                      )}
                    />
                    {suggestedWorkingConditionsValues &&
                      suggestedWorkingConditionsValues.maxYearlyHours && (
                        <Box position="absolute" style={{ right: '38px', top: '34px' }}>
                          <MatchingValueTooltip
                            type="workingCondition"
                            linked={
                              form.get('max_legal_yearly_hours').value ===
                              suggestedWorkingConditionsValues.maxYearlyHours
                            }
                            onClick={() =>
                              form
                                .get('max_legal_yearly_hours')
                                .set(suggestedWorkingConditionsValues.maxYearlyHours)
                            }
                            workingConditionName={workingConditions?.name}
                          />
                        </Box>
                      )}
                  </Box>
                  <Box flexDirection="row" alignItems="flexEnd" gap="s8" position="relative">
                    <TextInput
                      field={form.get('maximum_weekly_hours')}
                      type="number"
                      min={0}
                      label={translate(translations, 'max_weekly_hours')}
                      error={validateField('WEEKLY_HOURS', form.get('maximum_weekly_hours').value)}
                    />
                    {suggestedWorkingConditionsValues &&
                      suggestedWorkingConditionsValues.maxWeeklyHours && (
                        <Box position="absolute" style={{ right: '38px', top: '34px' }}>
                          <MatchingValueTooltip
                            type="workingCondition"
                            linked={
                              form.get('maximum_weekly_hours').value ===
                              suggestedWorkingConditionsValues.maxWeeklyHours
                            }
                            onClick={() =>
                              form
                                .get('maximum_weekly_hours')
                                .set(suggestedWorkingConditionsValues.maxWeeklyHours)
                            }
                            workingConditionName={workingConditions?.name}
                          />
                        </Box>
                      )}
                  </Box>
                  <Box flexDirection="row" alignItems="flexEnd" gap="s8" position="relative">
                    <TextInput
                      field={form.get('max_work_minutes_per_day')}
                      type="number"
                      min={0}
                      label={translate(translations, 'max_work_minutes_per_day')}
                      error={validateField(
                        'DAILY_HOURS',
                        form.get('max_work_minutes_per_day').value
                      )}
                    />
                    {suggestedWorkingConditionsValues &&
                      suggestedWorkingConditionsValues.maxDailyHours && (
                        <Box position="absolute" style={{ right: '38px', top: '34px' }}>
                          <MatchingValueTooltip
                            type="workingCondition"
                            linked={
                              form.get('max_work_minutes_per_day')?.value ===
                              suggestedWorkingConditionsValues?.maxDailyHours
                            }
                            onClick={() =>
                              form
                                .get('max_work_minutes_per_day')
                                .set(suggestedWorkingConditionsValues.maxDailyHours)
                            }
                            workingConditionName={workingConditions?.name}
                          />
                        </Box>
                      )}
                  </Box>
                  <Box flexDirection="row" alignItems="flexEnd" gap="s8" position="relative">
                    <TextInput
                      field={form.get('min_rest_minutes_between_days')}
                      type="number"
                      min={0}
                      label={translate(translations, 'min_rest_minutes_between_days')}
                      error={validateField(
                        'REST_HOURS_BETWEEN_DAYS',
                        form.get('min_rest_minutes_between_days').value
                      )}
                    />
                    {suggestedWorkingConditionsValues &&
                      suggestedWorkingConditionsValues.minRestHoursBetweenDays && (
                        <Box position="absolute" style={{ right: '38px', top: '34px' }}>
                          <MatchingValueTooltip
                            type="workingCondition"
                            linked={
                              form.get('min_rest_minutes_between_days')?.value ===
                              suggestedWorkingConditionsValues?.minRestHoursBetweenDays
                            }
                            onClick={() =>
                              form
                                .get('min_rest_minutes_between_days')
                                .set(suggestedWorkingConditionsValues.minRestHoursBetweenDays)
                            }
                            workingConditionName={workingConditions?.name}
                          />
                        </Box>
                      )}
                  </Box>
                </Box>
              )}
            </Box>
            <Box gap="s32" flexDirection="column">
              <Box gap="s16">
                <Box gap="s8">
                  <Box gap="s2">
                    <Text.Normal color="grey1000" weight="medium" size="200">
                      {translate(translations, 'bank_holidays_treatment')}
                    </Text.Normal>
                  </Box>
                  <Box gap="s8">
                    {bankHolidayTreatmentOptions.map((option) => (
                      <Box key={option.value} position="relative">
                        <RadioButton
                          label={option.label}
                          size="small"
                          onChange={() => form.get('bank_holiday_treatment').set(option.value)}
                          value={option.value}
                          checked={form.get('bank_holiday_treatment').value === option.value}
                        />
                        {option.value === form.get('bank_holiday_treatment').value &&
                          suggestedWorkingConditionsValues &&
                          suggestedWorkingConditionsValues.bankHolidaysTreatment && (
                            <Box position="absolute" style={{ right: '38px' }}>
                              <MatchingValueTooltip
                                type="workingCondition"
                                linked={
                                  form.get('bank_holiday_treatment').value ===
                                  suggestedWorkingConditionsValues.bankHolidaysTreatment
                                }
                                onClick={() =>
                                  form
                                    .get('bank_holiday_treatment')
                                    .set(suggestedWorkingConditionsValues.bankHolidaysTreatment)
                                }
                                workingConditionName={workingConditions?.name}
                              />
                            </Box>
                          )}
                      </Box>
                    ))}
                  </Box>
                </Box>
                {showAnnualWorkingTimeDistribution && (
                  <Box gap="s32" flexDirection="column">
                    <Box gap="s8">
                      <Box gap="s2">
                        <Text.Normal color="grey1000" weight="medium" size="200">
                          {translate(translations, 'annual_working_time_distribution')}
                        </Text.Normal>
                      </Box>
                      <Box gap="s8">
                        {annualWorkingTimeDistributionOptions.map((option) => (
                          <Box key={option.value} flexDirection="row" alignItems="center" gap="s8">
                            <RadioButton
                              key={option.value}
                              label={option.label}
                              size="small"
                              onChange={() =>
                                form.get('annual_working_time_distribution').set(option.value)
                              }
                              value={option.value}
                              checked={
                                form.get('annual_working_time_distribution').value === option.value
                              }
                            />
                            <InformationTooltip information={option.description} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box gap="s16">
                <Text size="400" weight="medium" color="grey1000">
                  {translate(translations, 'employment_type')}
                </Text>
                {sortedEmploymentFields.map((field) => (
                  <React.Fragment key={field.key}>{field.component}</React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
        </FormWrapper>
      </Box>
    ) : (
      <Box gap="s24">
        <FormWrapper
          withFormWrapper={withFormWrapper}
          hint={
            step && (
              <WorkHoursHint
                step={step}
                type={type}
                isContractHoursSet={isContractHoursSet}
                isWorkingFrequencySet={isWorkingFrequencySet}
              />
            )
          }
        >
          <Box flexDirection="row" gap="s16">
            <TextInput
              field={form.get('working_hours')}
              type="number"
              min={0}
              label={translate(translations, 'hours')}
            />
            <DropdownSearchBar
              name={workingHoursName()}
              label={translate(translations, 'frequency') || ''}
              onSelect={(item) => {
                form.get('working_hours_frequency').set(item.value)
              }}
              labelNotFound={i18n.t('contract_versions.forms.basic_information.option_not_found')}
              data={frequencyOptions}
              searchLabel={i18n.t(
                'contract_versions.forms.contract_extra_information.a3innuva.search_label'
              )}
              popoverWidth="s300"
            />
          </Box>
        </FormWrapper>
        {legalEntityCountry === 'es' && (
          <FormWrapper withFormWrapper={withFormWrapper}>
            <DropdownSearchBar
              name={workingType()}
              label={i18n.t(
                'add_employee_modal.contract_information_step.fields.working_day_type.label'
              )}
              onSelect={(item) => {
                workingDayTypeField?.set(item.value)
              }}
              labelNotFound={i18n.t(
                'contract_versions.forms.basic_information.work_day_type_not_found'
              )}
              data={workingDayTypesOptions}
              searchLabel={i18n.t(
                'contract_versions.forms.contract_extra_information.a3innuva.search_label'
              )}
              loading={loading}
              popoverWidth="s400"
            />
          </FormWrapper>
        )}
        <FormWrapper withFormWrapper={withFormWrapper}>
          <Box gap="s8">
            <Text.Normal color="grey1000" weight="normal" size="200">
              {translate(translations, 'days')}
            </Text.Normal>
            <Box paddingLeft="s24" alignItems="flexStart">
              <DaysSelector field={form.get('working_week_days')} canUnselectAll />
            </Box>
          </Box>
        </FormWrapper>
        {canSeeYearlyBalance && (
          <>
            <FormWrapper withFormWrapper={withFormWrapper} hint={step && <MaximumHoursHint />}>
              <Box flexDirection="row" gap="s16">
                <TextInput
                  field={form.get('max_legal_yearly_hours')}
                  type="number"
                  min={0}
                  label={translate(translations, 'max_legal_yearly_hours')}
                />
              </Box>
            </FormWrapper>
            <FormWrapper withFormWrapper={withFormWrapper}>
              <Box flexDirection="row" gap="s16">
                <TextInput
                  field={form.get('maximum_weekly_hours')}
                  type="number"
                  min={0}
                  label={translate(translations, 'maximum_weekly_hours')}
                />
              </Box>
            </FormWrapper>
          </>
        )}
      </Box>
    )
  }
)

const WorkHoursWrapper: FC<Props> = (props) => {
  const { translations } = props
  return (
    <Box gap="s32">
      <FormWrapper>
        <Text size="400" weight="medium" color="grey1000">
          {translate(translations || workingHoursTranslations(), 'header')}
        </Text>
      </FormWrapper>
      <WorkHours {...props} />
    </Box>
  )
}

export default observer(WorkHoursWrapper)
